import { useContext, useMemo, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import GeoJSON from 'ol/format/GeoJSON';
import * as helpers from '../../../../../../helpers/helpers';
import LocationComponent from '../../reportproblem/components/selectOptions/LocationComponent';
import ServiceType from './selectoptions/ServiceType';
import Department from './selectoptions/Department';
import { formatInTimeZone } from 'date-fns-tz';
import { getLabelByValue } from '../ProblemDetails';
import { AppInfoContext } from '../../../../../../App';
import DMA from '../../reportproblem/components/selectOptions/DMA';
import './StatisticsInformationModal.css';

function ReportTable({ setFullScreenTable }) {
  const { appInformation, setAppInformation } = useContext(AppInfoContext);

  const transform = (item) => {
    const geoJsonFormat = new GeoJSON();

    return geoJsonFormat.readFeature(
      {
        type: 'Feature',
        geometry: item.geom,
        properties: {
          id: item.id,
        },
      },
      {
        dataProjection: 'EPSG:3908',
        featureProjection: 'EPSG:3857',
      }
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: (info) => (
          <div
            style={{
              //   backgroundColor: '#21538f',
              backgroundColor:
                info.getValue() === 0
                  ? 'rgba(219, 231, 56, 1)'
                  : info.getValue() === 1
                  ? '#079C6A'
                  : 'rgb(38, 119, 196)',
              padding: '2px 6px',
              color: 'white',
              borderRadius: '6px',
              textAlign: 'center',
            }}
          >
            {info.getValue() === 0
              ? 'Neobrađen'
              : info.getValue() === 1
              ? 'U obradi'
              : 'Zatvoren'}
          </div>
        ),
      },

      {
        accessorKey: 'workOrder.serialNumber',
        header: 'Broj r.n.',
        minSize: 65,
        maxSize: 100,
      },
      {
        accessorKey: 'reportedAt',
        header: 'Datum prijave',
        minSize: 104,
        maxSize: 110,
        cell: (info) => (
          <div>
            {formatInTimeZone(
              info.getValue(),
              'Europe/Paris',
              'dd.MM.yyyy. HH:mm'
            )}
          </div>
        ),
      },
      {
        accessorFn: (row) =>
          `${row.user?.firstName ?? ''} ${row.user?.lastName ?? ''}`,
        header: 'Prijavio',
        minSize: 100,
        maxSize: 115,
      },
      {
        accessorKey: 'address',
        header: 'Adresa',
        minSize: 60,
        maxSize: 100,
      },
      {
        accessorKey: 'dma',
        header: 'DMA zona',
        minSize: 80,
        maxSize: 100,
        cell: (info) => {
          return <div>{getLabelByValue(info.getValue(), DMA)}</div>;
        },
      },
      {
        accessorKey: 'locationComponent',
        header: 'Pozicija',
        minSize: 130,
        maxSize: 140,
        cell: (info) => {
          const row = info.row.original;
          const value =
            row?.workOrder?.closedWorkOrder?.locationComponent ??
            row.locationComponent;

          return <div>{getLabelByValue(value, LocationComponent)}</div>;
        },
      },

      {
        accessorKey: 'workOrder.createdAt',
        header: 'Datum otvaranja r.n.',
        minSize: 130,
        maxSize: 140,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value
                ? formatInTimeZone(value, 'Europe/Paris', 'dd.MM.yyyy. HH:mm')
                : ''}
            </div>
          );
        },
      },

      {
        accessorKey: 'workOrder.closedWorkOrder.closedAt',
        header: 'Datum zatvaranja r.n.',
        minSize: 135,
        maxSize: 140,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value
                ? formatInTimeZone(value, 'Europe/Paris', 'dd.MM.yyyy. HH:mm')
                : ''}
            </div>
          );
        },
      },
      {
        accessorKey: 'workOrder.closedWorkOrder.serviceType',
        header: 'Tip usluge',
        minSize: 80,
        maxSize: 90,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value !== null && value !== undefined
                ? getLabelByValue(value, ServiceType)
                : ''}
            </div>
          );
        },
      },
      {
        accessorKey: 'workOrder.executionDepartment',
        header: 'Služba izvršenja',
        minSize: 100,
        maxSize: 120,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value !== null && value !== undefined
                ? getLabelByValue(value, Department)
                : ''}
            </div>
          );
        },
      },
      {
        accessorKey: 'workOrder.closedWorkOrder.startDate',
        header: 'Datum popravke - od',
        minSize: 140,
        maxSize: 150,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value
                ? formatInTimeZone(value, 'Europe/Paris', 'dd.MM.yyyy. HH:mm')
                : ''}
            </div>
          );
        },
      },
      {
        accessorKey: 'workOrder.closedWorkOrder.endDate',
        header: 'Datum popravke - do',
        minSize: 140,
        maxSize: 150,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value
                ? formatInTimeZone(value, 'Europe/Paris', 'dd.MM.yyyy. HH:mm')
                : ''}
            </div>
          );
        },
      },
      {
        accessorFn: (row) =>
          `${row?.workOrder?.closedWorkOrder?.user?.firstName ?? ''} ${
            row?.workOrder?.closedWorkOrder?.user?.lastName ?? ''
          }`,
        header: 'Zatvorio',
        minSize: 100,
        maxSize: 115,
      },
      {
        accessorKey: 'workOrder.closedWorkOrder.totalExpense',
        header: 'Cijena',
        minSize: 70,
        maxSize: 90,
        cell: (info) => {
          const value = info.getValue();
          return (
            <div>
              {value !== null && value !== undefined
                ? `${Number(value).toFixed(2)}`
                : ''}
            </div>
          );
        },
        sortingFn: (a, b, columnId) => {
          const aValue = Number(a.getValue(columnId)) || 0;
          const bValue = Number(b.getValue(columnId)) || 0;
          return aValue - bValue;
        },
      },
    ],
    []
  );

  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data: appInformation?.statistics?.data?.filteredProblems,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const rowHoverStyle = {
    transition: 'background-color 0.2s ease',
    cursor: 'pointer',
  };

  return (
    <table className="statistics-table-style ">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                onClick={header.column.getToggleSortingHandler()}
                className="statistics-table-th-td statistics-table-header-style"
                style={{
                  cursor: header.column.getCanSort() ? 'pointer' : 'default',
                  width: header.column.getSize(),
                  minWidth: header.column.columnDef.minSize,
                  maxWidth: header.column.columnDef.maxSize,
                  height: '30px',
                  position: 'sticky',
                  top: '0',
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
                {header.column.getIsSorted() === 'asc' ? ' 🔼' : ''}
                {header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, index) => (
          <tr
            key={row.id}
            style={{
              ...rowHoverStyle,
              backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = '#e6f7ff')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                index % 2 === 0 ? '#ffffff' : '#f9f9f9')
            }
            onClick={() => {
              setFullScreenTable(false);
              helpers.zoomToFeature(transform(row.original), true, true);
            }}
          >
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="statistics-table-th-td">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default ReportTable;
